"use client";

import React from "react";
import { ArrowRight, BookOpen, CircleCheck as CheckCircle2, Download, FileSpreadsheet, FileText, Layers, Lock, ChartPie as PieChart, ShieldCheck, TrendingUp, Users } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
interface FindingItem {
  id: number;
  stat: string;
  label: string;
  description: string;
}
interface TakeawayItem {
  id: number;
  iconName: string;
  title: string;
  detail: string;
}
interface RelatedReport {
  id: number;
  title: string;
  edition: string;
  pages: string;
  path: string;
}
const keyFindings: FindingItem[] = [{
  id: 1,
  stat: "68%",
  label: "Committee expansion",
  description: "Enterprise purchase decisions now involve an average of 9.2 stakeholders across finance, security, and operations."
}, {
  id: 2,
  stat: "41%",
  label: "Attribution shift",
  description: "B2B teams have reduced single-touch lead attribution in favor of multi-touch account journeys over 180-day cycles."
}, {
  id: 3,
  stat: "3.4x",
  label: "Content-led pipeline",
  description: "Deals influenced by peer-reviewed research and benchmark data showed significantly higher close velocities."
}, {
  id: 4,
  stat: "54%",
  label: "Budget realignments",
  description: "Enterprise marketing budgets favor verified first-party audience intelligence over third-party syndication networks."
}];
const takeaways: TakeawayItem[] = [{
  id: 1,
  iconName: "PieChart",
  title: "Resource & budget benchmark",
  detail: "Direct spending breakdown across 12 discrete B2B enterprise industry categories."
}, {
  id: 2,
  iconName: "Users",
  title: "Buying committee anatomy",
  detail: "Analysis of technical evaluators, executive sponsors, and procurement review timelines."
}, {
  id: 3,
  iconName: "TrendingUp",
  title: "Pipeline conversion dynamics",
  detail: "Empirical baseline ratios from initial intent signal to formal contract signing."
}, {
  id: 4,
  iconName: "FileSpreadsheet",
  title: "2026 tactical frameworks",
  detail: "Step-by-step scoring sheets and pipeline hygiene audits for enterprise demand teams."
}];
const relatedReports: RelatedReport[] = [{
  id: 1,
  title: "Enterprise Revenue Architecture & Pipeline Governance",
  edition: "Q1 2026 Executive Series",
  pages: "44 pages",
  path: "/ResearchReportsPage"
}, {
  id: 2,
  title: "B2B Editorial Authority & First-Party Data Strategy",
  edition: "Annual Benchmark Study",
  pages: "58 pages",
  path: "/ResearchReportsPage"
}];
function ReportAccessForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-md border border-border bg-muted/40 p-6 text-center">
        <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <CheckCircle2 className="size-5" />
        </div>
        <h3 className="font-serif text-lg font-semibold text-foreground">
          Access granted
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {successMessage || "Your download link has been sent to your business email."}
        </p>
        <div className="mt-5 flex flex-col gap-2.5 sm:flex-row sm:justify-center">
          <Button variant="default" size="sm" asChild>
            <Link to="/ResearchReportsPage" className="inline-flex items-center gap-1.5">
              <BookOpen className="size-4" />
              <span>Browse report library</span>
            </Link>
          </Button>
          <Button onClick={resetForm} variant="outline" size="sm">
            Request another copy
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Please enter your name"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-foreground">
              Full name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" className="bg-card text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="business_email" rules={{
      required: "Business email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid work email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-foreground">
              Business email
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@company.com" className="bg-card text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" rules={{
      required: "Company name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-foreground">
              Company
            </FormLabel>
            <FormControl>
              <Input placeholder="Acme Corp" className="bg-card text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-xs text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
        {isSubmitting ? <span className="flex items-center gap-2">
            <span className="size-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
            <span>Verifying access...</span>
          </span> : <span className="flex items-center justify-center gap-2">
            <Download className="size-4" />
            <span>Request full 76-page report (PDF)</span>
          </span>}
      </Button>

      <div className="flex items-center justify-center gap-1.5 pt-1 text-[11px] text-muted-foreground">
        <Lock className="size-3 text-muted-foreground" />
        <span>Strict zero-spam editorial policy. Peer-reviewed data only.</span>
      </div>
    </Form>;
}
export default function Spotlight() {
  const shouldReduceMotion = useReducedMotion();
  const getTakeawayIcon = (name: string) => {
    switch (name) {
      case "PieChart":
        return <PieChart className="size-5 text-foreground" />;
      case "Users":
        return <Users className="size-5 text-foreground" />;
      case "TrendingUp":
        return <TrendingUp className="size-5 text-foreground" />;
      case "FileSpreadsheet":
        return <FileSpreadsheet className="size-5 text-foreground" />;
      default:
        return <Layers className="size-5 text-foreground" />;
    }
  };
  return <section id="featured-report-spotlight" data-nav="light" className="bg-background py-16 text-foreground sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Section Masthead Header */}
        <div className="mb-10 sm:mb-14">
          <div className="flex flex-wrap items-center justify-between gap-3 pb-4">
            <div className="flex items-center gap-2.5">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Flagship Benchmark Study
              </span>
              <Separator orientation="vertical" className="h-3.5 bg-border" />
              <Badge variant="outline" className="rounded-sm border-border bg-muted/60 px-2 py-0.5 text-[11px] font-mono text-muted-foreground">
                Sample Content
              </Badge>
            </div>
            <span className="font-mono text-xs text-muted-foreground">
              Vol. 14 / Report Ref: MMR-2026-D1
            </span>
          </div>

          <div className="h-px w-full bg-border" />
          <div className="mt-1 h-[0.5px] w-full bg-border/60" />
        </div>

        {/* Main Grid: Visual & Summary (Left 7 cols) + Access Form (Right 5 cols) */}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Left Column: Cover & Editorial Analysis */}
          <div className="space-y-10 lg:col-span-7">
            {/* Title & Meta */}
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground">
                <span>Published January 2026</span>
                <span>•</span>
                <span>76 Pages</span>
                <span>•</span>
                <span>1,400 Enterprise Respondents</span>
              </div>
              <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                2026 State of B2B Demand Generation
              </h2>
              <p className="font-serif text-lg leading-relaxed text-muted-foreground sm:text-xl">
                An empirical examination of pipeline attribution, expanding stakeholder committees, and the shift from volume-driven funnels to precision account intelligence.
              </p>
            </div>

            {/* Publication Cover Artwork Mockup */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="relative overflow-hidden rounded-md border border-border bg-card p-6 shadow-md sm:p-8">
              {/* Publication spine effect */}
              <div className="pointer-events-none absolute inset-y-0 left-0 w-4 opacity-40" style={{
              background: "linear-gradient(90deg, rgba(0,0,0,0.18) 0%, rgba(255,255,255,0.12) 35%, transparent 100%)"
            }} />

              <div className="relative pl-3">
                <div className="flex items-center justify-between border-b border-border pb-4">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <BookOpen className="size-4" />
                    <span>MinfoMedia Research Institute</span>
                  </div>
                  <Badge variant="secondary" className="rounded-sm bg-secondary text-secondary-foreground font-mono text-[11px]">
                    Annual Edition
                  </Badge>
                </div>

                <div className="my-6 grid grid-cols-1 gap-6 sm:grid-cols-2 sm:items-center">
                  <div className="space-y-3">
                    <p className="font-mono text-xs text-muted-foreground uppercase">
                      Executive Briefing
                    </p>
                    <h3 className="font-serif text-xl font-semibold text-foreground">
                      Methodology & Field Sampling
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Double-blind surveyed leaders spanning SaaS, industrial technology, financial services, and telecom across North America and Western Europe.
                    </p>
                  </div>

                  <div className="rounded-md border border-border bg-muted/40 p-4 font-mono text-xs">
                    <div className="space-y-2 text-muted-foreground">
                      <div className="flex justify-between border-b border-border/60 pb-1">
                        <span>Format</span>
                        <span className="font-semibold text-foreground">PDF + Dataset</span>
                      </div>
                      <div className="flex justify-between border-b border-border/60 pb-1">
                        <span>Methodology</span>
                        <span className="font-semibold text-foreground">Quantitative</span>
                      </div>
                      <div className="flex justify-between border-b border-border/60 pb-1">
                        <span>Data verification</span>
                        <span className="font-semibold text-foreground">Peer Reviewed</span>
                      </div>
                      <div className="flex justify-between pt-1">
                        <span>Audit grade</span>
                        <span className="font-semibold text-foreground">Institutional</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck className="size-4 text-foreground" />
                  <span>Unsponsored editorial research under MinfoMedia Publishing Standards.</span>
                </div>
              </div>
            </motion.div>

            {/* Key Findings Bullet Grid */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl font-semibold text-foreground">
                  Key Research Findings
                </h3>
                <span className="text-xs font-mono text-muted-foreground">
                  4 Core Findings
                </span>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {keyFindings.map((finding, idx) => <motion.div key={finding.id} data-index={idx} initial={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: 12
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: idx * 0.08
              }} className="flex flex-col justify-between rounded-md border border-border bg-card p-5 shadow-xs transition-colors hover:border-border/80">
                    <div>
                      <span className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                        {finding.stat}
                      </span>
                      <h4 className="mt-1 font-serif text-base font-semibold text-foreground">
                        {finding.label}
                      </h4>
                      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                        {finding.description}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </div>

          {/* Right Column: High-contrast Form Container */}
          <div className="space-y-8 lg:col-span-5">
            <Card className="rounded-md border border-border bg-card p-6 shadow-md sm:p-8">
              <CardHeader className="p-0 pb-6">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="rounded-sm border-border bg-muted/50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Full Access
                  </Badge>
                  <span className="font-mono text-xs text-muted-foreground">
                    Instant PDF Release
                  </span>
                </div>
                <CardTitle className="mt-3 font-serif text-2xl font-semibold text-foreground">
                  Report Access Request
                </CardTitle>
                <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                  Receive the unabridged report including complete benchmark datasets, segment breakdowns, and tactical execution worksheets.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-0">
                <FormProvider formId="research-reports_featured-report-spotlight_form" sectionName="featured-report-spotlight" defaultValues={{
                full_name: "",
                business_email: "",
                company: ""
              }} validationRules={{
                full_name: {
                  required: true,
                  minLength: 2
                },
                business_email: {
                  required: true,
                  pattern: "email"
                },
                company: {
                  required: true
                }
              }} submitText="Request Full Report" successMessage="Thank you. The full research report has been dispatched to your work inbox." errorMessage="Unable to process your request. Please confirm your details and try again.">
                  <ReportAccessForm />
                </FormProvider>
              </CardContent>
            </Card>

            {/* Quick Overview Accordion / Checklist */}
            <div className="rounded-md border border-border bg-muted/30 p-6">
              <div className="flex items-center gap-2 pb-3">
                <FileText className="size-4 text-foreground" />
                <h4 className="font-serif text-base font-semibold text-foreground">
                  Included in this publication
                </h4>
              </div>
              <ul className="space-y-2.5 text-xs text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 shrink-0 text-foreground" />
                  <span>Comprehensive 76-page executive report with raw statistical appendices</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 shrink-0 text-foreground" />
                  <span>CSV format cross-tabulation datasets for internal modeling</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 shrink-0 text-foreground" />
                  <span>Executive summary slide deck ready for board and revenue leadership reviews</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* 'What You Will Learn' Takeaway Grid */}
        <div className="mt-16 sm:mt-20">
          <div className="mb-6 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Executive Takeaways
              </span>
              <h3 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">
                What you will learn from this study
              </h3>
            </div>
            <p className="max-w-md text-xs text-muted-foreground">
              Key operational insights engineered for Chief Revenue Officers, VP Demand Generation, and Revenue Operations leaders.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {takeaways.map((item, index) => <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.07
          }} className="flex flex-col justify-between rounded-md border border-border bg-card p-6 shadow-xs">
                <div>
                  <div className="mb-4 flex size-10 items-center justify-center rounded-sm bg-muted/60">
                    {getTakeawayIcon(item.iconName)}
                  </div>
                  <h4 className="font-serif text-base font-semibold text-foreground">
                    {item.title}
                  </h4>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                    {item.detail}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Related Research Links Strip */}
        <div className="mt-16 rounded-md border border-border bg-card p-6 sm:mt-20 sm:p-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Continuing Research
              </span>
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Related empirical reports & executive briefings
              </h3>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/ResearchReportsPage" className="inline-flex items-center gap-1.5">
                <span>View all reports</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            {relatedReports.map((report, rIdx) => <div key={report.id} data-index={rIdx} className="group flex flex-col justify-between rounded-md border border-border/80 bg-muted/20 p-5 transition-colors hover:border-border hover:bg-muted/40">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                    <span>{report.edition}</span>
                    <span>{report.pages}</span>
                  </div>
                  <h4 className="font-serif text-base font-semibold text-foreground group-hover:text-primary">
                    <Link to={report.path} className="inline-flex items-center gap-1.5">
                      <span>{report.title}</span>
                    </Link>
                  </h4>
                </div>

                <div className="mt-4 pt-3 border-t border-border/60">
                  <Link to={report.path} className="inline-flex items-center gap-1 text-xs font-semibold text-foreground hover:underline">
                    <span>Read overview & methodology</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}